/**
 * Turning patient-level record values into what the screen shows.
 *
 * Pure functions, separate from any component, for the reason `ward/wardDisplay.ts`
 * gave when they lived there: they are where the safety-critical decisions are —
 * which flags appear, in what order, and what a missing value reads as — and those
 * are worth testing without a DOM.
 *
 * **They moved here when patient search arrived (PHASE-0.md §2.3), and the move is
 * the point.** A search hit and a ward-list row are the same patient seen from two
 * screens; a second copy of `safetyBadges` would be two places for the allergy
 * precedence to be decided, and the day they disagreed one screen would show *Inga
 * kända allergier* for a patient the other flagged. `wardDisplay.ts` keeps only what
 * is genuinely about a ward list.
 *
 * **`t` is a parameter, not an import** (I18N.md §1). A module-level `t()` resolves
 * once, when the bundle is first evaluated, and freezes whatever language was
 * selected then — invisible with one catalogue and silent with two.
 *
 * The keys are all in `common`, which is what lets one function serve both screens:
 * a `TFunction<['common', 'ward']>` and a `TFunction<['common', 'patient']>` are both
 * assignable to {@link Translate}, and neither screen has to borrow the other's
 * namespace to render a badge.
 */

import type { TFunction } from 'i18next'

import { clinicalAgeYears } from '../format/clinicalAge'
import type {
  AllergyStatus,
  PatientBirthDate,
  PatientIdentifier,
  PatientNames,
  PatientSafety,
} from './patientRecord'

/**
 * The lookup these functions need: `common`, and nothing else.
 *
 * Deliberately the narrowest namespace list that works, because it is what makes
 * these functions shareable. A caller's `t` from `useTranslation(['common', …])` is
 * assignable to this; one that named its own namespace first would not be, which is
 * why every screen puts `common` first.
 */
export type Translate = TFunction<['common']>

/**
 * How loud a badge is. Colour is never the only signal — every badge carries text
 * (WCAG 1.4.1, ARCHITECTURE.md §13) — but the level decides the order and the
 * weight.
 */
export type SafetyLevel = 'critical' | 'warning' | 'neutral'

export interface SafetyBadge {
  /** Stable key for React, and for the tests to assert on. */
  readonly key: string
  readonly label: string
  readonly level: SafetyLevel
}

/**
 * Renders one value, or says plainly that it could not be rendered.
 *
 * The shared formatters throw rather than print something that could be misread, which
 * is right — but one malformed birth date must not blank the other twenty-nine beds and
 * their allergy flags. So the failure is contained to its own cell and labelled, never
 * quietly replaced with a dash.
 *
 * **`value.unreadable` is not `value.notRecorded`**, and keeping them apart is the
 * point of having two keys. "There is nothing recorded" and "there is something
 * recorded that this screen cannot show you" are different facts, and the second one is
 * a defect somebody has to report — a shared string would hide it as an empty field.
 *
 * Lived privately in `ward/WardList.tsx` and again in `patient/PatientSearch.tsx`, as
 * two identical copies, until the overview needed a third. It is here rather than in
 * either component for the same reason `safetyBadges` is: it decides what a clinician
 * reads when a clinical value cannot be shown, and that must not be able to differ
 * between screens.
 */
export function guarded(render: () => string, t: Translate): string {
  try {
    return render()
  } catch {
    return t('value.unreadable')
  }
}

/**
 * `Family, Given` — the order a list is read in and sorted by.
 *
 * An unidentified patient may have neither. Saying so is the only honest option:
 * an empty cell in a name column reads as a rendering fault, and the clinician
 * needs to know this is a patient nobody has named yet.
 */
export function patientName(patient: PatientNames, t: Translate): string {
  const family = patient.familyName ?? ''
  const given = patient.givenName ?? ''

  if (family === '' && given === '') {
    return t('identity.nameMissing')
  }

  return family === '' || given === '' ? `${family}${given}` : `${family}, ${given}`
}

/**
 * The identifier as recorded, or a statement that there is none.
 *
 * Never reformatted. A personnummer is copied, scanned and compared against a
 * wristband; inserting or removing a hyphen makes a visual comparison fail for a
 * number that matches. On a search hit this is the identifier that *matched*, which
 * is the API's decision and not this function's — see `PatientSearchHit`.
 */
export function patientIdentifier(patient: PatientIdentifier, t: Translate): string {
  return patient.identifierValue ?? t('value.notRecorded')
}

/**
 * Age in whole years with its unit, or a statement that the birth date is unknown.
 *
 * The number and its unit are **one** catalogue message, `{years} år`, not a number
 * concatenated with a translated `år`. I18N.md §8 forbids the concatenation, and the
 * reason is that the unit does not always follow the number — but note the argument
 * is bare. A typed `{years, number}` would put the age through Intl's number
 * formatting, which is the door AGENTS.md §3 rule 7 closes; `tests/catalogue.test.ts`
 * refuses that type in any message. (Named in prose rather than written out, because
 * `tests/noLocaleFormatting.test.ts` scans this file for the symbol itself — and
 * rightly, since this is a module that renders clinical values.)
 */
export function patientAge(
  patient: PatientBirthDate,
  t: Translate,
  today: Date = new Date(),
): string {
  if (patient.birthDate === null) {
    return t('value.notRecorded')
  }

  return t('identity.age', { years: clinicalAgeYears(patient.birthDate, today) })
}

/**
 * The allergy badge for one of the four states.
 *
 * A function rather than a lookup table, because a table built at module scope holds
 * labels in whatever language was selected when this module first evaluated.
 *
 * Exported because the patient overview's safety panel needs the same two facts — the
 * words and how loud they are — for the line it shows when there is no allergy row to
 * list (PHASE-0.md §2.4). A second mapping there would be a second place for *Inga
 * kända allergier* to be decided, which is the thing this module exists to prevent.
 */
export function allergyBadge(status: AllergyStatus, t: Translate): SafetyBadge {
  switch (status) {
    case 'active':
      return { key: 'allergy', label: t('safety.allergyActive'), level: 'critical' }
    case 'unknown':
      return { key: 'allergy', label: t('safety.allergyUnknown'), level: 'warning' }
    case 'no-known-allergies':
      return { key: 'allergy', label: t('safety.allergyNoneKnown'), level: 'neutral' }
    case 'undocumented':
      // Reachable, and routinely. The fixture test enforces the trichotomy for the
      // reviewed ward only; generated ballast has no allergy row and no
      // allergy-status flag at all, so every patient `--admit` puts on a ward lands
      // here — all 24 of `K2` do (docs/STATUS.md, 2026-09-30). Shown loudly, because
      // the alternative is a cell that looks like "no allergies".
      return { key: 'allergy', label: t('safety.allergyUndocumented'), level: 'warning' }
  }
}

/**
 * The badges for one patient, in the order they are read.
 *
 * **The allergy badge is always present.** That is the point of the trichotomy: a
 * row that says nothing about allergies is indistinguishable from a row that says
 * there are none, and the two are very different facts (AGENTS.md §5).
 *
 * Critical before warning before neutral, so the thing that changes treatment is
 * leftmost on a row a clinician scans across.
 */
export function safetyBadges(patient: PatientSafety, t: Translate): readonly SafetyBadge[] {
  const badges: SafetyBadge[] = []

  // First, before even the allergy: everything else on the row is read differently once it is
  // known (DECISIONS.md #53). The patient stays in the bed, so the list has to say it.
  if (patient.deceased) {
    badges.push({ key: 'deceased', label: t('safety.deceased'), level: 'critical' })
  }

  badges.push(allergyBadge(patient.allergyStatus, t))

  if (patient.dnr) {
    badges.push({ key: 'dnr', label: t('safety.dnr'), level: 'critical' })
  }

  if (patient.isolation !== null) {
    // The isolation type is record content and is shown as it was written — never
    // translated, never mapped to a house vocabulary (AGENTS.md §3 rule 8). It goes
    // through the message as a bare ICU argument, which is plain String(value), and
    // the label travels with it so a language that puts it the other way round can.
    badges.push({
      key: 'isolation',
      label: t('safety.isolation', { type: patient.isolation }),
      level: 'warning',
    })
  }

  if (patient.isEstimatedIdentity) {
    // An unidentified patient's name and birth date are a working assumption, and
    // the list has to say so — treating a believed identity as established is how
    // records get merged into the wrong patient.
    badges.push({
      key: 'estimated-identity',
      label: t('safety.estimatedIdentity'),
      level: 'warning',
    })
  }

  if (patient.confidentialityMarked) {
    badges.push({
      key: 'confidentiality',
      label: t('safety.confidentialityMarked'),
      level: 'warning',
    })
  }

  const weight: Record<SafetyLevel, number> = { critical: 0, warning: 1, neutral: 2 }

  // A stable sort, which Array.prototype.sort is required to be: two warnings keep
  // the order above, so the badges do not move between renders of the same row.
  return [...badges].sort((left, right) => weight[left.level] - weight[right.level])
}
